import { makeAutoObservable } from 'mobx';
import { v4 as uuidv4 } from 'uuid';

export class ProcessingStore {
    private processes = new Map<
        string,
        {
            inProgress: boolean;
            percentage: number;
            startPercentage: number;
            endPercentage: number;
            totalSegments: number;
            completedSegments: number;
            autoTickIntervalId?: ReturnType<typeof setInterval>;
        }
    >();

    constructor() {
        makeAutoObservable(this);
    }

    start() {
        // Start processing the whole pipeline

        // generate a UUID for the process
        const id: string = uuidv4();

        // Generate new process
        this.processes.set(id, {
            inProgress: true,
            percentage: 0,
            startPercentage: 0,
            endPercentage: 0,
            totalSegments: 0,
            completedSegments: 0,
            autoTickIntervalId: undefined,
        });
        return id;
    }

    setCompleted(processId: string) {
        const p = this.processes.get(processId);
        p.percentage = 100;
        p.inProgress = false;
        return;
    }

    getPercentage(processId: string): number {
        const p = this.processes.get(processId);
        return p.percentage;
    }

    startPart(endPercentage: number, totalSegments: number, processId: string) {
        const p = this.processes.get(processId);
        this.stopPart(p.percentage, processId);
        p.startPercentage = p.percentage;
        p.endPercentage = endPercentage;
        p.totalSegments = totalSegments;
        const intervalMs = this.calcIntervalFor(p.totalSegments, p.startPercentage, p.endPercentage);
        this.startAutoTick(endPercentage, intervalMs, processId);
        return;
    }

    stopPart(endPercentage: number, processId: string) {
        this.stopAutoTick(processId);
        const p = this.processes.get(processId);
        p.percentage = endPercentage;
        return;
    }

    tick(processId: string, increment = 1) {
        const p = this.processes.get(processId);
        p.completedSegments = p.completedSegments + increment;
        const nextPercentage = p.startPercentage + (p.completedSegments / p.totalSegments) * (p.endPercentage - p.startPercentage);
        p.percentage = Math.max(Math.round(nextPercentage), p.percentage);
        return;
    }

    startAutoTick(endPercentage: number, intervalMs: number, processId: string) {
        const p = this.processes.get(processId);
        if (p.autoTickIntervalId) {
            clearInterval(p.autoTickIntervalId);
        }
        p.autoTickIntervalId = setInterval(() => {
            p.percentage = Math.min(p.percentage + 1, endPercentage);
            if (p.percentage >= endPercentage) {
                this.stopAutoTick(processId);
            }
        }, intervalMs);
        return;
    }

    stopAutoTick(processId: string) {
        const p = this.processes.get(processId);
        if (p.autoTickIntervalId) {
            clearInterval(p.autoTickIntervalId);
            p.autoTickIntervalId = undefined;
        }
        return;
    }

    calcIntervalFor(totalSegments: number, startPercentage: number, endPercentage: number) {
        let segmentDurationMs: number;
        if (totalSegments <= 1) {
            segmentDurationMs = 20000;
        } else if (totalSegments <= 10) {
            segmentDurationMs = 10000;
        } else {
            segmentDurationMs = 3000;
        }
        const partPercentage = endPercentage - startPercentage;
        const partDurationMs = totalSegments * segmentDurationMs;
        return Math.round(partDurationMs / partPercentage);
    }
}

export const processingStore = new ProcessingStore();
